import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import AdminBackButton from "@/components/AdminBackButton";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, AlertTriangle, Clock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return Math.round((mins / 60) * 100) / 100;
}

export default function DelaysPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [items, setItems] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [equipmentHours, setEquipmentHours] = useState([]);
  const [delayCodes, setDelayCodes] = useState([]);
  const [report, setReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ equipment_id: "", delay_code_id: "", start_time: "", end_time: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const loadData = async () => {
    try {
      const [entries, eqHours, lookups, r] = await Promise.all([
        api.get(`/reports/${reportId}/delay-events`),
        api.get(`/reports/${reportId}/equipment-hours`),
        api.get("/lookups"),
        api.get(`/reports/${reportId}`),
      ]);
      setItems(entries || []);
      setEquipmentHours(eqHours || []);
      setEquipment(lookups.equipment || []);
      setDelayCodes(lookups.delay_codes || []);
      setReport(r);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [reportId]);

  const isReadOnly = report?.status === "Submitted";
  const getEquipName = (id) => equipment.find(e => e.id === id)?.unit_id || "—";
  const getDelayInfo = (id) => delayCodes.find(d => d.id === id);

  // Auto-fill time from Equipment Hours when equipment is selected
  const handleEquipmentChange = (equipId) => {
    const updatedForm = { ...form, equipment_id: equipId };
    if (!editItem && equipId) {
      const matchingEqHour = equipmentHours.find(eh => eh.equipment_id === equipId);
      if (matchingEqHour) {
        if (!form.start_time) updatedForm.start_time = matchingEqHour.start_time || "";
        if (!form.end_time) updatedForm.end_time = matchingEqHour.end_time || "";
        if (!form.operator_name && matchingEqHour.operator_name) updatedForm.operator_name = matchingEqHour.operator_name;
        if (matchingEqHour.start_time || matchingEqHour.end_time || matchingEqHour.operator_name) {
          setAutoFilled(true);
        }
      }
    }
    setForm(updatedForm);
  };

  const openAdd = () => {
    setEditItem(null);
    setAutoFilled(false);
    setForm({ equipment_id: "", delay_code_id: "", start_time: "", end_time: "", operator_name: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      equipment_id: item.equipment_id || "",
      delay_code_id: item.delay_code_id || "",
      start_time: item.start_time || "",
      end_time: item.end_time || "",
      operator_name: item.operator_name || "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.delay_code_id || !form.start_time || !form.end_time) {
      toast.error("Delay code, start time, and end time are required");
      return;
    }
    setSaving(true);
    const hours = calcHours(form.start_time, form.end_time);
    const payload = { ...form, delay_hours: hours };
    try {
      if (editItem) {
        await api.put(`/reports/${reportId}/delay-events/${editItem.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post(`/reports/${reportId}/delay-events`, payload);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reports/${reportId}/delay-events/${id}`);
      toast.success("Entry deleted");
      await loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen" data-testid="delays-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <AdminBackButton />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-400" strokeWidth={1.5} /> Delays
            </h1>
            <p className="text-muted-foreground text-sm mt-1"><span className="kpi-value">{items.length}</span> entries</p>
          </div>
          {!isReadOnly && (
            <Button onClick={openAdd} data-testid="add-delay-button" className="font-headline font-semibold">
              <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground text-sm">No delays logged yet</CardContent></Card>
        ) : (
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase">Code</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Description</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Equipment</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Start</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">End</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Hours</TableHead>
                  {!isReadOnly && <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const dc = getDelayInfo(item.delay_code_id);
                  return (
                    <TableRow key={item.id} className="border-border" data-testid={`delay-row-${item.id}`}>
                      <TableCell className="font-mono text-sm font-medium">{dc?.code || "—"}</TableCell>
                      <TableCell className="text-sm">{dc?.description || "—"}</TableCell>
                      <TableCell className="text-sm">{getEquipName(item.equipment_id)}</TableCell>
                      <TableCell className="font-mono text-sm">{item.start_time || "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{item.end_time || "—"}</TableCell>
                      <TableCell className="kpi-value text-sm">{item.delay_hours?.toFixed(2) || "—"}</TableCell>
                      {!isReadOnly && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md" data-testid="delay-dialog">
            <DialogHeader>
              <DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Delay Event</DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">Record equipment downtime or operational delays</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Delay Code *</Label>
                <Select value={form.delay_code_id} onValueChange={(v) => setForm({ ...form, delay_code_id: v })}>
                  <SelectTrigger data-testid="delay-code-select" className="bg-background border-border"><SelectValue placeholder="Select delay code" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {delayCodes.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        <span className="font-mono">{d.code}</span> — {d.description} ({d.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Equipment (Optional)</Label>
                <Select value={form.equipment_id} onValueChange={handleEquipmentChange}>
                  <SelectTrigger data-testid="delay-eq-select" className="bg-background border-border"><SelectValue placeholder="Select equipment (optional)" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.unit_id} — {e.equipment_type}</SelectItem>)}
                  </SelectContent>
                </Select>
                {autoFilled && (
                  <p className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="delay-autofill-hint">
                    <Clock className="h-3 w-3" strokeWidth={1.5} /> Pre-filled from Equipment Hours
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Start Time *</Label>
                  <Input data-testid="delay-start-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-background border-border font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">End Time *</Label>
                  <Input data-testid="delay-end-time" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-background border-border font-mono" />
                </div>
              </div>
              {form.start_time && form.end_time && (
                <p className="text-sm text-muted-foreground">Duration: <span className="kpi-value text-foreground">{calcHours(form.start_time, form.end_time).toFixed(2)} hrs</span></p>
              )}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Operator Name</Label>
                <Input data-testid="delay-operator-name" value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} placeholder="Operator name" className="bg-background border-border" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Notes</Label>
                <Input data-testid="delay-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="bg-background border-border" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-delay-button" className="font-headline font-semibold">{saving ? "Saving..." : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
